import { Component, forwardRef, Input, OnInit, input, output, viewChild } from "@angular/core";
import { NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { SelectItem } from "primeng/api";
import { Observable, takeUntil } from "rxjs";
import { Community } from "../../../types/organization";
import { PCPCommunitySelectorBaseComponent } from '../pcp-community-selector.basecomponent';

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { AutoCompleteModule } from "primeng/autocomplete";
import { FluidModule } from "primeng/fluid";

@Component({
	selector: "pcp-community-autocomplete",
	templateUrl: "./pcp-community-autocomplete.component.html",
	styleUrls: [],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => PCPCommunityAutoCompleteComponent),
		}
	],
	standalone: true,
	imports: [AutoCompleteModule, FormsModule, FluidModule, ErrormessageComponent]
})
export class PCPCommunityAutoCompleteComponent extends PCPCommunitySelectorBaseComponent {
	readonly communitySelection = viewChild<NgModel>("communitySelection");

	communities: Community[]

	_observable: Observable<Community[]>;

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set communitiesObservable(observable: Observable<Community[]>) {
		this._observable = observable;
		this.reloadOptions();
	};

	get communitiesObservable(): Observable<Community[]> {
		return this._observable;
	}

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input() placeholder: string = "Communities";
	readonly forceSelection = input<boolean>(true);

	_selectedCommunity: Community;

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set selectedCommunity(community: Community) {
		this._selectedCommunity = community;
		this.propagateChange(this._selectedCommunity);
	};

	get selectedCommunity(): Community {
		return this._selectedCommunity;
	}

	readonly selectedCommunityChange = output<Community>();
	readonly communitiesLoaded = output<any>();

	readonly onCompleteFilter = input<(filterString: string, communitiesToFilter: Community[]) => void>(this.filter);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input() communitySuggestions: Community[] = [];

	readonly excludeNoAssociatedCommunityOption = input<boolean>(false);

	constructor() {
		super();
	}

	writeValue(obj: any): void {
		this.selectedCommunity = obj;
	}

	onChange() {
		this.selectedCommunityChange.emit(this.selectedCommunity);
	}

	onComplete(event: any) {
		const filterString: string = event && event.query || "";
		const fn = this.onCompleteFilter();
		if (fn) {
			fn.call(this, filterString, this.communities);
		}
	}

	filter(filterString: string, communities: Community[]) {
		const theFilterWasCleared: boolean = filterString === "";
		if (theFilterWasCleared) {
			this.communitySuggestions = this.communitiesToAutoCompleteSuggestions(this.communities);
			return;
		}


		const filteredCommunities = communities.filter((communityOption: Community) => {

			if (this.excludeNoAssociatedCommunityOption()) {
				return communityOption.displayText.toLowerCase().indexOf(filterString.toLowerCase()) !== -1 && communityOption.orgId > 0;
			}

			return communityOption.displayText.toLowerCase().indexOf(filterString.toLowerCase()) !== -1;
		});

		this.communitySuggestions = filteredCommunities;
	}

	clearCommunity() {
		this.selectedCommunity = null;
	}

	communitiesToAutoCompleteSuggestions(communities: Community[]) {
		const byDisplayText = (a, b) => a.displayText > b.displayText ? 1 : -1;
		return communities.map((community: Community) => {
			community.displayText = this.getCommunityDisplayText(community);
			return community;
		}).sort(byDisplayText);
	}


	selectCommunity(community: Community, addIfNotPresent: boolean = false) {
		if (!community) { return; }

		const communityIsNotPresent = !this.communitySuggestions.some((comm) => comm.orgId === community.orgId);
		if (communityIsNotPresent && addIfNotPresent) {
			this.communities.unshift(community);
			this.communitySuggestions = this.communitiesToAutoCompleteSuggestions(this.communities);
		}

		this.selectedCommunity = this.communitySuggestions.find((comm) => comm.orgId === community.orgId);
		this.selectedCommunityChange.emit(this.selectedCommunity);
	}

	selectNoAssociatedCommunity() {
		this.selectedCommunity = this.communities.find((comm) => comm.orgId === 0);
		this.selectedCommunityChange.emit(this.selectedCommunity);
	}

	reloadOptions(): void {
		if (!this.communitiesObservable) { return; }

		const originalPlaceholder = this.placeholder;
		this.placeholder = "Loading...";
		this.communitiesObservable
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((communities) => {
				this.communities = communities;
				this.communitySuggestions = this.communitiesToAutoCompleteSuggestions(this.communities);
				this.placeholder = originalPlaceholder;
				// TODO: The 'emit' function requires a mandatory any argument
				this.communitiesLoaded.emit(null);
			});
	}

}
